import { useState } from 'react';
import type { ResponsibleVacationGroup, ClientVacationPeriod } from '../../../models/vacationClientView.types';
import { ClientPeriodCard } from './ClientPeriodCard';
import { CollapsibleRegion } from './CollapsibleRegion';
import { ResponsibleSectionHeader } from './ResponsibleSectionHeader';
import { APPROVED_SECTION_TITLE, PENDING_SECTION_TITLE } from '../vacationClientPanel.definitions';

interface ResponsibleSectionProps {
    responsible: ResponsibleVacationGroup;
    clientId: string;
    clientName?: string;
    onOpenRequest?: (collaboratorId: string) => void;
    initiallyExpanded?: boolean;
}

interface PeriodSectionProps {
    title: string;
    periods: ClientVacationPeriod[];
    allPeriods: ClientVacationPeriod[];
    responsibleName: string;
    clientName?: string;
    onOpenRequest?: (collaboratorId: string) => void;
    expandFirst?: boolean;
}

function PeriodSection({ title, periods, allPeriods, responsibleName, clientName, onOpenRequest, expandFirst = false }: PeriodSectionProps) {
    if (periods.length === 0) return null;

    return (
        <div>
            <p className="px-0 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-raro-gray-500 font-montserrat">
                {title}
            </p>
            <div className="space-y-2 pb-3">
                {periods.map((period, index) => (
                    <ClientPeriodCard
                        key={period.requestId}
                        period={period}
                        clientName={clientName}
                        responsibleName={responsibleName}
                        siblingPeriods={allPeriods}
                        onOpenRequest={onOpenRequest}
                        initiallyExpanded={expandFirst && index === 0}
                    />
                ))}
            </div>
        </div>
    );
}

export function ResponsibleSection({
    responsible,
    clientId,
    clientName,
    onOpenRequest,
    initiallyExpanded = false,
}: ResponsibleSectionProps) {
    const [isOpen, setIsOpen] = useState(initiallyExpanded);
    const bodyId = `responsible-body-${clientId}-${responsible.responsibleKey}`;
    const pendingPeriods = responsible.periods.filter((p) => p.isPending);
    const approvedPeriods = responsible.periods.filter((p) => !p.isPending);

    return (
        <div className="rounded-lg border border-raro-gray-100 bg-raro-gray-50 shadow-raro overflow-hidden">
            <ResponsibleSectionHeader
                responsible={responsible}
                isOpen={isOpen}
                controlsId={bodyId}
                onToggle={() => setIsOpen((prev) => !prev)}
            />
            <CollapsibleRegion id={bodyId} isOpen={isOpen}>
                <div className="space-y-4 px-4 py-4">
                    <PeriodSection
                        title={PENDING_SECTION_TITLE}
                        periods={pendingPeriods}
                        allPeriods={responsible.periods}
                        responsibleName={responsible.responsibleName}
                        clientName={clientName || ''}
                        onOpenRequest={onOpenRequest}
                        expandFirst={initiallyExpanded}
                    />
                    <PeriodSection
                        title={APPROVED_SECTION_TITLE}
                        periods={approvedPeriods}
                        allPeriods={responsible.periods}
                        responsibleName={responsible.responsibleName}
                        clientName={clientName || ''}
                        onOpenRequest={onOpenRequest}
                    />
                </div>
            </CollapsibleRegion>
        </div>
    );
}

export default ResponsibleSection;
